import React from "react";
import {
  ActivityIndicator,
  StyleProp,
  StyleSheet,
  View,
  ViewStyle
} from "react-native";
import { colors } from "@/constants";

/**
 * Props for the Spinner component.
 */
interface SpinnerProps {
  /** Indicator size. Defaults to "large" — the common case for a loading
   *  view. Use "small" inline next to a button or in a card. */
  size?: "small" | "large";
  /** Color override. Defaults to the brand primary; override only when the
   *  surrounding surface requires different contrast (e.g. a tinted card). */
  color?: string;
  /** Container style. Use this to express how much space the spinner should
   *  occupy and where (flex / padding / absolute positioning / background).
   *  The container is always centered on both axes — that part is intrinsic
   *  to the spinner and doesn't belong in the call site. */
  style?: StyleProp<ViewStyle>;
}

/**
 * Branded loading indicator. Wraps React Native's `ActivityIndicator` with
 * the brand color and self-centering so consumers only have to express
 * layout (how much space, where) — not "and please center it."
 *
 * @example
 * ```tsx
 * // Fill available space — e.g. an empty screen waiting for data.
 * <Spinner style={{ flex: 1 }} />
 *
 * // Bounded inline loader inside a ScrollView.
 * <Spinner style={{ paddingVertical: layout.space.xl }} />
 *
 * // Inline next to a label.
 * <Spinner size="small" />
 *
 * // Full-screen blocking overlay.
 * <Spinner style={StyleSheet.absoluteFill} />
 * ```
 */
export default function Spinner({
  size = "large",
  color = colors.primary,
  style
}: SpinnerProps) {
  return (
    <View
      style={[styles.container, style]}
      accessibilityRole="progressbar"
      accessibilityLabel="Loading"
    >
      <ActivityIndicator size={size} color={color} />
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: "center",
    justifyContent: "center"
  }
});
